import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Search, Globe, User } from "lucide-react";
import { NAV_ITEMS } from "../../config/navigation";
import { COMPANY } from "../../config/company";
import { useLanguage, LANGUAGES } from "../../context/LanguageContext";

function Logo({ dark }) {
  return (
    <img
      src={dark ? "/tessa-logo.png" : "/tessa-logo-light.png"}
      alt="TESSA Corporation"
      className="h-7 md:h-8 w-auto select-none"
      draggable="false"
    />
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const lenis = useLenis();
  const reduce = useReducedMotion();

  // Close the language menu when clicking anywhere else
  useEffect(() => {
    if (!langOpen) return;
    const close = () => setLangOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [langOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const dark = scrolled; // dark text when solid ivory bg

  const goTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) {
      if (lenis) lenis.scrollTo(el, { offset: -72 });
      else el.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
    } else {
      // anchor lives on the home page — route there with the hash
      window.location.assign(`/${href}`);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          backgroundColor: scrolled ? "rgba(246,242,236,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(37,37,37,0.08)" : "1px solid transparent",
          transition:
            "background-color 0.5s ease, border-color 0.5s ease, backdrop-filter 0.5s ease",
        }}
        data-testid="site-header"
      >
        <div className="container-tessa flex items-center justify-between h-[72px] md:h-20">
          <button onClick={() => goTo("#home")} data-testid="logo-home-link" aria-label="TESSA home">
            <Logo dark={dark} />
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => goTo(item.href)}
                data-testid={`nav-${item.id}`}
                className="link-underline text-[13px] uppercase tracking-[0.14em] font-medium transition-colors"
                style={{ color: dark ? "#252525" : "#F6F2EC" }}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </nav>

          {/* Right utilities */}
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => navigate("/catalog")}
              data-testid="search-toggle"
              aria-label={t("nav.search")}
              className="hidden md:grid place-items-center h-9 w-9 rounded-full transition-colors hover:bg-black/5"
              style={{ color: dark ? "#252525" : "#F6F2EC" }}
            >
              <Search size={18} strokeWidth={1.6} />
            </button>

            {/* Task 25 — five-language switcher */}
            <div className="relative hidden md:block">
              <button
                onClick={(e) => { e.stopPropagation(); setLangOpen((v) => !v); }}
                data-testid="language-toggle"
                aria-label="Change language"
                aria-expanded={langOpen}
                className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors"
                style={{ color: dark ? "#252525" : "#F6F2EC" }}
              >
                <Globe size={16} strokeWidth={1.6} />
                <span>{lang}</span>
              </button>
              {langOpen && (
                <div
                  data-testid="language-menu"
                  className="absolute right-0 top-full mt-3 min-w-[150px] bg-white border border-black/10 shadow-xl py-1 z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`language-option-${l.code}`}
                      aria-pressed={lang === l.code}
                      className={`w-full text-left px-4 py-2.5 text-[12px] uppercase tracking-[0.12em] transition-colors flex items-center justify-between gap-3 ${
                        lang === l.code ? "text-[#D92A47] font-semibold" : "text-[#252525] hover:bg-[#F6F2EC]"
                      }`}
                    >
                      <span>{l.label}</span>
                      <span className="opacity-40">{l.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => navigate("/admin")}
              data-testid="client-access"
              aria-label={t("nav.clientAccess")}
              className="hidden md:grid place-items-center h-9 w-9 rounded-full transition-colors hover:bg-black/5"
              style={{ color: dark ? "#252525" : "#F6F2EC" }}
            >
              <User size={18} strokeWidth={1.6} />
            </button>

            {/* Mobile toggle */}
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-expanded={open}
              aria-label="Menu"
              className="lg:hidden grid place-items-center h-10 w-10"
              style={{ color: open ? "#F6F2EC" : dark ? "#252525" : "#F6F2EC" }}
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 lg:hidden bg-black flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex-1 flex flex-col justify-center container-tessa gap-1">
              {NAV_ITEMS.map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.5 }}
                  onClick={() => goTo(item.href)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="text-left font-serif text-4xl sm:text-5xl text-[#F6F2EC] py-3 border-b border-white/10"
                >
                  {t(item.labelKey)}
                </motion.button>
              ))}

              <div className="mt-10">
                <div className="flex items-center gap-4 flex-wrap" data-testid="mobile-language-switcher">
                  <Globe size={16} className="text-[#C4A46B]" />
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      data-testid={`mobile-language-${l.code}`}
                      aria-pressed={lang === l.code}
                      className={`text-sm font-semibold uppercase tracking-[0.14em] transition-colors ${
                        lang === l.code ? "text-[#C4A46B]" : "text-[#F6F2EC]/50 hover:text-[#F6F2EC]"
                      }`}
                    >
                      {l.code}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setOpen(false); navigate("/admin"); }}
                  data-testid="mobile-client-access"
                  className="mt-6 flex items-center gap-2 text-[#F6F2EC]/70 text-sm uppercase tracking-[0.14em]"
                >
                  <User size={16} /> {t("nav.clientAccess")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
